// General functions that could be re-used in components

import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AbstractControl } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { isDevMode } from '@angular/core';
import { Location } from '@angular/common';

// For scrolling function
const TIMINGFUNC_MAP = {
	linear: t => t,
	'ease-in': t => t * t,
	'ease-out': t => t * (2 - t),
	'ease-in-out': t => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t)
};

// For Google Addresses
const componentLookup = {
	'street_number' : 'street',
	'route': 'number',
	'locality': 'city',
	'administrative_area_level_1': 'state',
	'postal_code': 'zip'
};

@Injectable()
export class UtilsService {
	constructor(
		private router: Router,
		private spinner: NgxSpinnerService,
		private location: Location
	) {}

	capitalizeAndSplit(str) {
		str = str
			.replace(/([A-Z])/g, ' $1')
			.trim()
			.split(' ');
		for (let i = 0, x = str.length; i < x; i++) {
			str[i] = str[i][0].toUpperCase() + str[i].substr(1);
		}
		return str.join(' ');
	}

	capitalize(str) {
		return str.replace(/^\w/, c => c.toUpperCase());
	}

	equalLength(a, b) {
		return a.value.length === b.value.length ? true : false;
	}

	navigateTo(state: string, params?: {}, delay?: number) {
		if (!params) params = {};
		setTimeout(() => {
			this.router.navigate([state.toString(), params]);
		}, delay || 0);
	}

	passwordMatcher(c: AbstractControl) {
		const password = c.get('password');
		const confirmPassword = c.get('confirmPassword');
		if (!password || !confirmPassword) return null;
		return password.value === confirmPassword.value ? null : { nomatch: true };
	}

	scrollToTop() {
		this.scrollTopSmooth(window.scrollY, 300, 'ease-in-out');
	}

	scrollToBottom() {
		window.scrollTo(0, document.body.scrollHeight);
	}

	scrollTopSmooth(initY, duration = 300, timingName = 'linear') {
		const timingFunc = TIMINGFUNC_MAP[timingName];
		let start = null;
		const step = timestamp => {
			start = start || timestamp;
			const progress = timestamp - start,
				time = Math.min(1, (timestamp - start) / duration);

			window.scrollTo(0, initY - timingFunc(time) * initY);
			if (progress < duration) {
				window.requestAnimationFrame(step);
			}
		};
		window.requestAnimationFrame(step);
	}

	removeDuplicates(arr) {
		let unique_array = [];
		for (let i = 0; i < arr.length; i++) {
			if (unique_array.indexOf(arr[i]) === -1) {
				unique_array.push(arr[i]);
			}
		}
		return unique_array;
	}

	cleanObject(obj) {
		for (let propName in obj) {
			if (obj[propName] === null || obj[propName] === undefined || obj[propName] === '') {
				delete obj[propName];
			}
		}
	}

	// Google Addresses
	formatAddressComponents(addressComponents: any[], cb: Function) {
		const ignoreComponents: Array<any> = ['administrative_area_level_2', 'administrative_area_level_3', 'postal_code_suffix', 'country'];
		let formattedAddress;
		let formCompatibleAddress = {
			streetOne: '',
			city: '',
			state: '',
			zip: ''
		};
		formattedAddress = addressComponents.filter(function(component) {
			return (!ignoreComponents.includes(component.types[0]));
		}).map(function(addressElem) {
			return {
				type: componentLookup[addressElem.types[0]],
				value: addressElem.short_name
			};
		}).forEach(function(newComponent) {
			if (newComponent.type === 'street') {
				formCompatibleAddress.streetOne += newComponent.value;
			} else if (newComponent.type === 'number') {
				formCompatibleAddress.streetOne += ' ' + newComponent.value;
			} else {
				formCompatibleAddress[newComponent.type] = newComponent.value;
			}
		});
		// this.populateAddressForm(formCompatibleAddress, formName);
		cb(formCompatibleAddress);
	}

	cleanAddress(a) {
		// return a.streetOne + (a.streetTwo ? (', ' + a.streetTwo) : '') + '<br>' + a.city + ', ' + a.state + ', ' + a.zip;
		return a.streetOne + (a.streetTwo ? (', ' + a.streetTwo) : '') + ', ' + a.city + ', ' + a.state + ', ' + a.zip;
	}

	showSpinner() {		
		this.spinner.show();
	}

	hideSpinner(delay?) {
		setTimeout(() => {
			this.spinner.hide();
		}, delay);
	}

	goBack() {
		this.location.back();		
	}

	logData(title = 'Data: ', data: any) {
		if (isDevMode()) {
			console.log(title, data);
		}
	}

	logJSON(title: string, data: any) {
		if (isDevMode()) {
			console.group(title);
			console.table(data);
			console.groupEnd();
		}
	}
}
